import cn from "classnames";

import { CheckmarkThinIcon } from "@/components/icons/checkmarkThin.icon";
import {
  RichText,
  type RichTextProps,
} from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { TwoColumnBenefitRowInput } from "./twoColumnImageBenefits.component";

const benefitBodyClassName =
  "text-greyscale-body min-w-0 font-sans [font-feature-settings:'liga'_off,'clig'_off] max-lg:text-[15px] max-lg:leading-[130%]";

const mobileSectionTitleClassName =
  "text-greyscale-headline mb-1.5 font-sans [font-feature-settings:'liga'_off,'clig'_off] lg:hidden";

/** Mobile: viewport-wide hairline with 20px inset. Desktop: spans text column only. */
const benefitDividerRuleClassName =
  "ml-[calc(50%-_50vw)] box-border w-screen max-w-[100vw] shrink-0 px-[20px] lg:ml-0 lg:w-full lg:max-w-none lg:px-0";

const benefitDividerLineClassName = "h-px w-full bg-greyscale-strength/45";

export const TwoColumnImageBenefitsCopy = ({
  title,
  lines,
  listStyle,
  legalText,
  isProductDetail,
}: TwoColumnImageBenefitsCopyProps) => {
  return (
    <div
      className={cn("flex min-w-0 flex-col justify-center", {
        "max-lg:bg-greyscale-background order-2 w-full max-lg:px-6 max-lg:pt-0 max-lg:pb-10 sm:max-lg:px-8 lg:flex-1":
          isProductDetail,
        "order-2 w-full": !isProductDetail,
      })}>
      <div
        className={cn("flex w-full flex-col", {
          "max-w-xl lg:mx-0 lg:max-w-none": isProductDetail,
          "mx-auto max-w-xl lg:mx-0 lg:max-w-lg xl:max-w-xl": !isProductDetail,
        })}>
        <Text
          variant="h4"
          component="h2"
          className={cn(
            "text-greyscale-headline [font-feature-settings:'liga'_off,'clig'_off] tracking-tight",
            "max-lg:mt-5 max-lg:mb-6 lg:mb-10",
          )}>
          {title}
        </Text>

        {listStyle === "dividers" ? (
          <ul className="m-0 flex list-none flex-col gap-0 p-0">
            {lines.map((line, index) => (
              <li key={line._key} className="flex flex-col">
                {index > 0 ? (
                  <div
                    aria-hidden
                    className={cn(benefitDividerRuleClassName, "mt-4 md:mt-6")}>
                    <div className={benefitDividerLineClassName} />
                  </div>
                ) : null}
                <div className={cn(index > 0 && "pt-4 md:pt-6")}>
                  {line.mobileSectionTitle ? (
                    <Text
                      variant="h6"
                      component="p"
                      className={mobileSectionTitleClassName}>
                      {line.mobileSectionTitle}
                    </Text>
                  ) : null}
                  <div className={cn(benefitBodyClassName, "max-w-none")}>
                    <RichText>{line.text}</RichText>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="flex flex-col gap-4 md:gap-6">
            {lines.map((line) => (
              <li key={line._key} className="flex items-start gap-3 md:gap-3.5">
                <span
                  className="text-greyscale-headline flex shrink-0 items-start"
                  aria-hidden>
                  <CheckmarkThinIcon className="block h-6 w-6 shrink-0" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  {line.mobileSectionTitle ? (
                    <Text
                      variant="h6"
                      component="p"
                      className={mobileSectionTitleClassName}>
                      {line.mobileSectionTitle}
                    </Text>
                  ) : null}
                  <div className={cn(benefitBodyClassName, "max-w-none")}>
                    <RichText>{line.text}</RichText>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {Array.isArray(legalText) && legalText.length > 0 ? (
          <div className="text-greyscale-body mt-10 max-w-none">
            <RichText>{legalText}</RichText>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export type TwoColumnImageBenefitsCopyProps = {
  title: string;
  lines: TwoColumnBenefitRowInput[];
  listStyle: "checkmarks" | "dividers";
  legalText?: RichTextProps["children"];
  isProductDetail: boolean;
};
